import React from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Form, Input } from '@flightaware/ui-components';

import { User } from 'models/user';
import { useAuth } from 'services/authentication';
import { createDashboard, updateDashboard } from 'api/dashboards';
import { Dashboard } from 'models/dashboard';
import { DashboardSettingsFormData } from 'models/dashboardSettingsFormData';
import { capitalizeString, parseCommaSeparatedFilterValue } from 'utils/format';
import { objectDiff } from 'utils/object';

import ManageDashboardUsers from 'components/ManageDashboardUsers';

interface ManageDashboardProps {
    dashboard: Dashboard;
    action: 'create' | 'update';
}

const dashboardSchema = (user: User | undefined) =>
    yup.object().shape({
        name: yup.string().required('Field is required.'),
        timezone: yup.mixed().required('Field is required.'),
        sortBy: yup.mixed().required('Field is required.'),
        // uiTheme
        pollDelay: yup
            .number()
            .typeError('Please enter a number.')
            .required('Field is required.')
            .min(user?.permissions.minimumPollDelay ?? 30, (rule) => `Refresh rate must be at least ${rule.min}`)
            .integer(),
        operatorDisplayFormat: yup.mixed().required('Field is required.'),
        alertThresholdEarly: yup
            .number()
            .typeError('Please enter a number.')
            .required('Field is required.')
            .min(0)
            .integer(),
        alertThresholdLate: yup
            .number()
            .typeError('Please enter a number.')
            .required('Field is required.')
            .min(0)
            .integer(),
        filterOperators: yup.mixed(),
        filterAirports: yup.mixed(),
        filterRegistrations: yup.mixed(),
        maximumFlights: yup
            .number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .typeError('Please enter a number.')
            .positive('Must be positive.')
            .integer('Must be an integer.'),
        showFlightsScheduledToArriveWithin: yup
            .number()
            .typeError('Please enter a number.')
            .required('Field is required.')
            .min(0, 'Must be great than zero.')
            .integer('Must be an integer.'),
        hideArrivedFlightsAfter: yup
            .number()
            .typeError('Please enter a number.')
            .required('Field is required.')
            .min(0, 'Must be great than zero.')
            .integer('Must be an integer.'),
    });

const useUpdateDashboard = (dashboardId: string) => {
    const queryClient = useQueryClient();

    return useMutation<Dashboard, Error, Partial<Dashboard>>((changes) => updateDashboard(dashboardId, changes), {
        onSuccess: () => {
            queryClient.resetQueries({ queryKey: ['dashboards'] });
        },
    });
};

const useCreateDashboard = () => {
    const queryClient = useQueryClient();

    return useMutation<Dashboard, Error, Partial<Dashboard>>((newDashboard) => createDashboard(newDashboard), {
        onSuccess: () => {
            queryClient.resetQueries({ queryKey: ['dashboards'] });
        },
    });
};

const getDashboardFromFormData = (formData: DashboardSettingsFormData): Partial<Dashboard> => {
    const dashboard: Partial<Dashboard> = {
        name: formData.name,
        timezone: formData.timezone.value,
        // uiTheme: formData.uiTheme,
        sortBy: formData.sortBy.value,
        pollDelay: formData.pollDelay,
        operatorDisplayFormat: formData.operatorDisplayFormat.value,
        alerts: {
            thresholdEarly: formData.alertThresholdEarly,
            thresholdLate: formData.alertThresholdLate,
        },
        filters: {
            operators:
                formData.filterOperators !== undefined
                    ? formData.filterOperators?.map((operator) => operator.value) || []
                    : parseCommaSeparatedFilterValue(formData.filterOperatorsAny),
            airports:
                formData.filterAirports !== undefined
                    ? formData.filterAirports?.map((airport) => airport.value) || []
                    : parseCommaSeparatedFilterValue(formData.filterAirportsAny),
            registrations:
                formData.filterRegistrations !== undefined
                    ? formData.filterRegistrations?.map((registration) => registration.value) || []
                    : parseCommaSeparatedFilterValue(formData.filterRegistrationsAny),
            maximumFlights: formData.maximumFlights,
            showFlightsScheduledToArriveWithin: formData.showFlightsScheduledToArriveWithin,
            hideArrivedFlightsAfter: formData.hideArrivedFlightsAfter,
        },
    };

    return dashboard;
};

export default function ManageDashboard(props: ManageDashboardProps) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { mutate } = props.action === 'update' ? useUpdateDashboard(props.dashboard.id) : useCreateDashboard();

    const isAdmin = user?.permissions.role === 'admin';
    const showManageDashboardUsers = isAdmin && props.action === 'update';

    return (
        <div className="manage fixed-width" data-testid="manage-dashboard">
            <h1>{capitalizeString(props.action)} dashboard</h1>
            <div className={showManageDashboardUsers ? 'half-width' : ''}>
                <Form
                    onSubmit={(data: DashboardSettingsFormData) => {
                        const newDashboard = getDashboardFromFormData(data);
                        const changes =
                            props.action === 'update' ? objectDiff(props.dashboard, newDashboard) : newDashboard;
                        mutate(changes, {
                            onError: (error) => toast.error(`Error saving dashboard settings: ${error.message}`),
                            onSuccess: (dashboard) => {
                                toast.success('Dashboard settings saved');
                                navigate(`/dashboards/${dashboard.id}`);
                            },
                        });
                    }}
                    onReset={() => navigate(-1)}
                    validationSchema={dashboardSchema(user)}
                    buttonPosition="left"
                    submitButton={{ show: true, text: 'Save', size: 'large' }}
                    resetButton={{ show: true, text: 'Cancel', size: 'large' }}
                    customClass="settings__form"
                >
                    <Input
                        type="text"
                        name="name"
                        label="Name"
                        placeholder="KBJC Arrivals"
                        defaultValue={props.dashboard.name}
                    />
                    <Input
                        type="select"
                        name="timezone"
                        label="Timezone display"
                        placeholder="UTC"
                        defaultValue={props.dashboard.timezone}
                        isClearable={false}
                        options={[
                            {
                                label: 'UTC',
                                value: 'utc',
                            },
                            {
                                label: 'User locale',
                                value: 'browser',
                            },
                            {
                                label: 'Airport locale',
                                value: 'airport',
                            },
                        ]}
                    />

                    <Input
                        type="select"
                        name="sortBy"
                        label="Default sort by"
                        placeholder="Predicted IN"
                        defaultValue={props.dashboard.sortBy || undefined}
                        isClearable={false}
                        options={[
                            { label: 'Ident - ASC', value: 'ident-asc' },
                            { label: 'Ident - DESC', value: 'ident-desc' },
                            { label: 'Registration - ASC', value: 'registration-asc' },
                            { label: 'Registration - DESC', value: 'registration-desc' },
                            { label: 'Origin - ASC', value: 'origin-asc' },
                            { label: 'Origin - DESC', value: 'origin-desc' },
                            { label: 'Destination - ASC', value: 'destination-asc' },
                            { label: 'Destination - DESC', value: 'destination-desc' },
                            { label: 'Estimated ON - ASC', value: 'estimatedOn-asc' },
                            { label: 'Estimated ON - DESC', value: 'estimatedOn-desc' },
                            { label: 'Predicted ON - ASC', value: 'predictedOn-asc' },
                            { label: 'Predicted ON - DESC', value: 'predictedOn-desc' },
                            { label: 'Predicted ON Difference - ASC', value: 'predictedOnDifference-asc' },
                            { label: 'Predicted ON Difference - DESC', value: 'predictedOnDifference-desc' },
                            { label: 'Estimated IN - ASC', value: 'estimatedIn-asc' },
                            { label: 'Estimated IN - DESC', value: 'estimatedIn-desc' },
                            { label: 'Predicted IN - ASC', value: 'predictedIn-asc' },
                            { label: 'Predicted IN - DESC', value: 'predictedIn-desc' },
                            { label: 'Predicted IN Difference - ASC', value: 'predictedInDifference-asc' },
                            { label: 'Predicted IN Difference - DESC', value: 'predictedInDifference-desc' },
                        ]}
                    />

                    <Input
                        type="numeric"
                        name="pollDelay"
                        label="Refresh rate (in seconds)"
                        placeholder="60"
                        defaultValue={props.dashboard.pollDelay}
                    />

                    <Input
                        type="select"
                        name="operatorDisplayFormat"
                        label="Operator display format"
                        placeholder="Operator display format"
                        defaultValue={props.dashboard.operatorDisplayFormat}
                        isClearable={false}
                        options={[
                            {
                                label: 'ICAO',
                                value: 'icao',
                            },
                            {
                                label: 'IATA',
                                value: 'iata',
                            },
                        ]}
                    />

                    <Input
                        type="numeric"
                        name="alertThresholdEarly"
                        label="Show alert if arrival prediction is earlier than estimated (in seconds)"
                        placeholder="Early alert threshold"
                        defaultValue={props.dashboard.alerts.thresholdEarly}
                    />

                    <Input
                        type="numeric"
                        name="alertThresholdLate"
                        label="Show alert if arrival prediction is later than estimated (in seconds)"
                        placeholder="Late alert threshold"
                        defaultValue={props.dashboard.alerts.thresholdLate}
                    />

                    {!isAdmin && (user?.permissions.filters.operators || []).length > 0 ? (
                        <Input
                            type="select"
                            name="filterOperators"
                            label="Operators to show"
                            placeholder="Operators to show"
                            defaultValue={props.dashboard.filters.operators}
                            multiple
                            options={(user?.permissions.filters.operators || []).map((operator) => ({
                                label: operator,
                                value: operator,
                            }))}
                        />
                    ) : (
                        <Input
                            type="text"
                            name="filterOperatorsAny"
                            label="Operators to show"
                            placeholder="EZY, EZS"
                            defaultValue={(props.dashboard?.filters.operators || []).join(', ')}
                        />
                    )}

                    {!isAdmin && (user?.permissions.filters.airports || []).length > 0 ? (
                        <Input
                            type="select"
                            name="filterAirports"
                            label="Airports to show"
                            placeholder="Airports to show"
                            defaultValue={props.dashboard.filters.airports}
                            multiple
                            options={(user?.permissions.filters.airports || []).map((airport) => ({
                                label: airport,
                                value: airport,
                            }))}
                        />
                    ) : (
                        <Input
                            type="text"
                            name="filterAirportsAny"
                            label="Airports to show"
                            placeholder="EGLL, KIAH"
                            defaultValue={(props.dashboard?.filters.airports || []).join(', ')}
                        />
                    )}

                    {!isAdmin && (user?.permissions.filters.registrations || []).length > 0 ? (
                        <Input
                            type="select"
                            name="filterRegistrations"
                            label="Registrations to show"
                            placeholder="Registrations to show"
                            defaultValue={props.dashboard.filters.registrations}
                            multiple
                            options={(user?.permissions.filters.registrations || []).map((registration) => ({
                                label: registration,
                                value: registration,
                            }))}
                        />
                    ) : (
                        <Input
                            type="text"
                            name="filterRegistrationsAny"
                            label="Registrations to show"
                            placeholder="YL-LDN, N24980"
                            defaultValue={(props.dashboard?.filters.registrations || []).join(', ')}
                        />
                    )}

                    <Input
                        type="numeric"
                        name="maximumFlights"
                        label="Maximum flights to show"
                        placeholder="Maximum flights to show"
                    />

                    <Input
                        type="numeric"
                        name="showFlightsScheduledToArriveWithin"
                        label="Show flights that are scheduled to arrive within (in seconds)"
                        placeholder="300"
                        defaultValue={props.dashboard.filters.showFlightsScheduledToArriveWithin}
                    />

                    <Input
                        type="numeric"
                        name="hideArrivedFlightsAfter"
                        label="Hide arrived flights after (in seconds)"
                        placeholder="300"
                        defaultValue={props.dashboard.filters.hideArrivedFlightsAfter}
                    />
                </Form>
            </div>
            {showManageDashboardUsers && (
                <div className={showManageDashboardUsers ? 'half-width' : ''}>
                    <ManageDashboardUsers dashboard={props.dashboard} />
                </div>
            )}
        </div>
    );
}

ManageDashboard.propTypes = {
    dashboard: PropTypes.object,
};
